import restClient from "./restClient";
import { toSecureUploadApiPath, toUploadRelativePath } from "utils/mediaUrl";

/**
 * Fetch an upload file via authenticated API and return a blob object URL.
 * Caller must revoke with URL.revokeObjectURL when done.
 */
export const fetchSecureMediaBlobUrl = async (uploadPathOrUrl) => {
  const apiPath = toSecureUploadApiPath(uploadPathOrUrl);
  if (!apiPath) {
    throw new Error("Invalid media path");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("Authentication required");
  }

  const response = await restClient.get(apiPath, {
    responseType: "blob",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const blob = response.data;
  if (!(blob instanceof Blob)) {
    throw new Error("Invalid media response");
  }

  return {
    blobUrl: URL.createObjectURL(blob),
    contentType: blob.type || response.headers?.["content-type"] || "",
    blob,
  };
};

/**
 * Fetch a publicly shareable file (no login) via signed media-share token.
 */
export const fetchSharedMediaBlobUrl = async (shareToken) => {
  const token = String(shareToken || "").trim();
  if (!token) {
    throw new Error("Invalid share token");
  }

  const response = await restClient.get(
    `media-shares/${encodeURIComponent(token)}`,
    { responseType: "blob" }
  );

  const blob = response.data;
  if (!(blob instanceof Blob)) {
    throw new Error("Invalid media response");
  }

  return {
    blobUrl: URL.createObjectURL(blob),
    contentType: blob.type || response.headers?.["content-type"] || "",
    blob,
  };
};

/**
 * Mint a frontend share URL: https://dealertiq.com/media/share/{token}
 * Works without login when opened; same lightbox UI when logged in.
 */
export const createExternalMediaShareUrl = async (uploadPathOrUrl) => {
  const uploadPath = toUploadRelativePath(uploadPathOrUrl);
  if (!uploadPath) {
    throw new Error("Invalid media path");
  }

  const authToken = localStorage.getItem("token");
  if (!authToken) {
    throw new Error("Authentication required");
  }

  const response = await restClient.post(
    "media-shares",
    { path: uploadPath },
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  );

  const shareToken = response?.data?.token;
  if (!shareToken || typeof shareToken !== "string") {
    throw new Error("Share token missing");
  }

  const origin =
    (typeof window !== "undefined" && window.location?.origin) ||
    process.env.REACT_APP_APP_BASE_URL ||
    "";
  if (!origin) {
    throw new Error("App origin missing");
  }

  return `${origin}/media/share/${encodeURIComponent(shareToken)}`;
};
